import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Col,
  ModalProps,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { CheckOutlined, CopyOutlined, EditOutlined } from '@ant-design/icons';
import copy from 'copy-text-to-clipboard';
import {
  ExportCSV,
  InfoButton,
  Label,
  ModalDialog,
  SpaceCell,
  SiteContent,
  message,
} from 'components/common';
import { COMMON_MSG_DEFAULT_DISPLAY_DURATION, DATAGRID_CELL_ICON_WIDTH1 } from 'constants/config';
import { fetchWarehousePostedTransferDetail } from 'services/inventory';
import { convertTimeByUtc } from 'utils';
import { batchStatusLabel, editTransferBatch, isErrorExist, submitTransferBatch } from '.';
import TransferLineGrid, { TransferLineGridMode, exportColumns, mapCsvData } from './TransferLineGrid';

type Props = ModalProps & {
  batchNumber: number;
  lockScreen?: Function;
  onSubmit?: Function;
};

const TransferBatchDetailDialog = (props: Props) => {
  const { useState } = React;
  const [inited, setInited] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [transferBatch, setTransferBatch] = useState<StringKAnyVPair>({});
  const history = useHistory();

  const attrLabelClass = () => {
    const cls = ['tail-colon2', 'text-end', 'w-35'];

    return cls.join(' ');
  };

  // eslint-disable-next-line
  const loadDetail = async (bn: number) => {
    lockScreen(true);
    try {
      const detail = await fetchWarehousePostedTransferDetail(bn);

      if (detail) {
        console.log('d', detail);
        setTransferBatch(detail);
      }
    } catch(e) {
    } finally {
      lockScreen(false);
    }
  };

  const lockScreen = (lock: boolean) => {
    if (props.lockScreen) props.lockScreen(lock);
  };

  const onClose = () => {
    // @ts-ignore
    props.onCancel();
  };

  const onDialogFullscreen = (fullscreen: boolean) => {
    setIsFullscreen(fullscreen);
  };

  const renderFooter = () => {
    return (
      <Row className="pt-8" justify="space-between">
        <Space>
        <InfoButton
          disabled={transferBatch.status !== 0}
          onClick={() => editTransferBatch(history, transferBatch.postedTransferNum)}
        >
          <EditOutlined />
          Edit
        </InfoButton>
          {transferBatch.totalLine > 0 && (
            <ExportCSV
              columns={exportColumns}
              data={mapCsvData(transferBatch.lines)}
              scope={transferBatch.batchNumber}
            />
          )}
        </Space>
        <Button
          disabled={transferBatch.status !== 0}
          onClick={submitBatch}
          type="primary"
        >
          <CheckOutlined />
          Submit
        </Button>
      </Row>
    );
  };

  const renderLineGrid = () => {
    return (<>
      <div
        className="mt-8 n-c-d-h-100"
        style={{height: `calc(100vh - ${isFullscreen ? 240 : 340}px)`}}
      >
        {transferBatch.postedTransferNum && (
          <TransferLineGrid
            mode={TransferLineGridMode.View}
            transferNumber={transferBatch.postedTransferNum}
            //version={gridVersion}
          />
        )}
      </div>
    </>);
  };

  const renderSummary = () => {
    return (<>
      <Row>
        <Col span={8}>
          <Row align="middle">
            <Label className={attrLabelClass()}>Batch Number</Label>
            <Label className="label-grey">{transferBatch.batchNumber}</Label>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Reference Number</Label>
            <Label className="label-grey">{transferBatch.referenceNumber}</Label>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>From Warehouse</Label>
            <Label className="label-grey">{transferBatch.fromWarehouseCode}</Label>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>To Warehouse</Label>
            <Label className="label-grey">{transferBatch.toWarehouseCode}</Label>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Status</Label>
            <Label className="label-grey">{batchStatusLabel(transferBatch.status)}</Label>
          </Row>
        </Col>
        <Col span={8}>
          <Row align="middle">
            <Label className="tail-colon2">Group Note</Label>
            <Label className="label-grey">{transferBatch.note}</Label>
          </Row>
          {isErrorExist(transferBatch) && (
            <Row align="middle" className="n-c-d-w-100">
              <SpaceCell
                icon={<>
                  <Tooltip
                    placement="top"
                    title="Copy error"
                    trigger={['hover', 'click']}
                  >
                    <CopyOutlined
                      className="text-info"
                      onClick={() => {
                        copy(transferBatch.error);
                        message.info({
                          content: 'Error has been copied to clipboard',
                          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
                        });
                      }}
                    />
                  </Tooltip>
                </>}
                iconWidth={DATAGRID_CELL_ICON_WIDTH1}
                text={(
                  <span className="text-danger">
                    <Label className="tail-colon2">Error</Label>
                    {transferBatch.error}
                  </span>
                )}
                //tooltip={typeof transferBatch.error === 'string' ? transferBatch.error.replace(/\n/g, '<br />') : transferBatch.error}
                tooltip={transferBatch.error}
              />
            </Row>
          )}
        </Col>
        <Col span={8}>
          <Row align="middle">
            <Label className={attrLabelClass()}>Create By</Label>
            <Typography.Text className="text-grey" ellipsis={{ tooltip: transferBatch.createBy }}>
              {transferBatch.createBy}
            </Typography.Text>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Create Date</Label>
            <Label className="label-grey">{convertTimeByUtc(transferBatch.createDate)}</Label>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Update By</Label>
            <Typography.Text className="text-grey" ellipsis={{ tooltip: transferBatch.updateBy }}>
              {transferBatch.updateBy}
            </Typography.Text>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Update Date</Label>
            <Label className="label-grey">{convertTimeByUtc(transferBatch.updateDate)}</Label>
          </Row>
        </Col>
      </Row>
    </>);
  };

  const submitBatch = () => {
    submitTransferBatch(
      transferBatch,
      lockScreen,
      (batch: StringKAnyVPair) => {
        if (props.onSubmit) props.onSubmit(batch);

        message.success({
          content: 'Submitted successfully',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
        onClose();
      },
    );
  };

  React.useEffect(() => {
    if (!inited) {
      loadDetail(props.batchNumber);
      setInited(true);
    }
  }, [inited, loadDetail, props]);

  return (<>
    <ModalDialog
      centered
      closeButton
      closable={false}
      destroyOnClose={true}
      footer={null}
      fullscreen
      maskClosable={false}
      onClose={onClose}
      onFullscreen={onDialogFullscreen}
      title="Posted Transfer Detail"
      visible={props.visible}
      width={1200}
    >
      <SiteContent className="common-content">
        {transferBatch.postedTransferNum > 0 && renderSummary()}
        {transferBatch.postedTransferNum > 0 && renderLineGrid()}
        {transferBatch.postedTransferNum > 0 && renderFooter()}
      </SiteContent>
    </ModalDialog>
  </>);
};

export default TransferBatchDetailDialog;
